/**
 * Public referral partner portal (no auth token in JWT — uses X-Partner-Token).
 * Landing at /partner?token=... — persists token in localStorage.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Loader2, Send, Handshake, TrendingUp, LogOut } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from '../hooks/use-toast';
import PhoneInput from './shared/PhoneInput';
import MoneyInput from './shared/MoneyInput';
import { brandAssetUrl } from '../branding';
import { PARTNER_KIND_LABELS, labelFor } from './shared/labels';
import { LINK_PAUSED_COPY, isLinkPaused, publicLinkError } from './errors';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const PARTNER_TOKEN_KEY = 'partner_token';

function makeApi(token) {
  return axios.create({
    baseURL: `${BACKEND_URL}/api/partner`,
    headers: { 'X-Partner-Token': token, 'Content-Type': 'application/json' },
  });
}

const INVALID_TOKEN_MESSAGE = 'That access token is not valid or has expired. Check the link you were sent, or ask your loan officer for a new one.';
const LOAN_PURPOSES = [
  ['purchase', 'Purchase'], ['refinance', 'Refinance'], ['cash_out', 'Cash-out refinance'],
  ['heloc', 'HELOC'], ['construction', 'Construction'], ['other', 'Other'],
];
const URGENCIES = [['low', 'Low'], ['normal', 'Normal'], ['high', 'High']];
const BLANK_FORM = {
  borrower_first_name: '', borrower_last_name: '',
  borrower_email: '', borrower_phone: '',
  loan_purpose: 'purchase', estimated_price: '', notes: '', urgency: 'normal',
};
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const UNREACHABLE_MESSAGE = 'We could not reach the portal. Check your connection and try again.';

// The link carries the token in its query string. Read it once, then take it
// out of the address bar so it does not sit in the history or get copied from
// a screenshot or a shared screen.
function takeTokenFromUrl() {
  const params = new URLSearchParams(window.location.search);
  const fromUrl = params.get('token');
  if (fromUrl) {
    params.delete('token');
    const rest = params.toString();
    window.history.replaceState(window.history.state, '', window.location.pathname + (rest ? `?${rest}` : '') + window.location.hash);
  }
  if (fromUrl) {
    // Saved before it is checked, so a dropped connection on the first load
    // does not lose it once it is out of the address bar. A rejected token is removed again in load().
    localStorage.setItem(PARTNER_TOKEN_KEY, fromUrl);
  }
  return fromUrl || localStorage.getItem(PARTNER_TOKEN_KEY) || '';
}

export default function PartnerPortalPage() {
  const [initialTok] = useState(takeTokenFromUrl);
  const [token, setToken] = useState(initialTok);
  const [draft, setDraft] = useState(initialTok);
  const [authError, setAuthError] = useState('');
  const [me, setMe] = useState(null);
  const [brand, setBrand] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(BLANK_FORM);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const api = makeApi(token);
      const [m, b, r] = await Promise.all([
        api.get('/me').then(x => x.data),
        api.get('/co-branded-page').then(x => x.data),
        api.get('/referrals').then(x => x.data),
      ]);
      setMe(m); setBrand(b); setReferrals(r.items || []);
      localStorage.setItem(PARTNER_TOKEN_KEY, token);
      setAuthError('');
    } catch (e) {
      setMe(null);
      // 401 = unknown token; 410 = expired or turned off by the loan officer.
      // 403 = the workspace is suspended (ticket 315): paused, so the token is kept.
      if (isLinkPaused(e)) {
        setAuthError(LINK_PAUSED_COPY);
      } else if ([401, 410].includes(e?.response?.status)) {
        // Only a rejected token is thrown away; a dropped connection keeps it for the retry.
        localStorage.removeItem(PARTNER_TOKEN_KEY);
        setAuthError(INVALID_TOKEN_MESSAGE);
      } else {
        setAuthError(UNREACHABLE_MESSAGE);
      }
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const submitToken = () => {
    const next = draft.trim();
    if (!next) return;
    if (next === token) load(); else setToken(next);
  };

  const signOut = () => {
    localStorage.removeItem(PARTNER_TOKEN_KEY);
    setToken(''); setDraft(''); setMe(null); setBrand(null); setReferrals([]); setAuthError('');
  };

  // The server needs both names; an email, when given, has to be one.
  const emailBad = !!form.borrower_email.trim() && !EMAIL_RE.test(form.borrower_email.trim());
  const canSubmit = !!form.borrower_first_name.trim() && !!form.borrower_last_name.trim() && !emailBad;

  const submit = async () => {
    setSubmitting(true);
    try {
      const api = makeApi(token);
      await api.post('/referrals', {
        ...form,
        borrower_first_name: form.borrower_first_name.trim(),
        borrower_last_name: form.borrower_last_name.trim(),
        borrower_email: form.borrower_email.trim() || null,
        borrower_phone: form.borrower_phone || null,
        estimated_price: form.estimated_price ? Number(form.estimated_price) : null,
      });
      toast({ title: 'Referral submitted' });
      setForm(BLANK_FORM);
      load();
    } catch (e) { toast({ title: 'Failed', description: publicLinkError(e, String(e)) }); }
    finally { setSubmitting(false); }
  };

  if (!token || !me) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-slate-50" data-testid="partner-token-page">
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-8 max-w-md w-full">
          <div className="flex items-center gap-3 mb-4">
            <Handshake style={{ color: '#070B5F' }} size={28} />
            <h1 className="font-serif text-3xl font-bold" style={{ color: '#070B5F' }}>Partner Portal</h1>
          </div>
          <p className="text-sm text-slate-500 mb-4">Enter your partner access token to continue.</p>
          <Input value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') submitToken(); }} placeholder="Access token" data-testid="partner-token-input" />
          {authError && <p className="text-sm text-red-700 mt-2" role="alert" data-testid="partner-token-error">{authError}</p>}
          <button onClick={submitToken} disabled={loading || !draft.trim()} data-testid="partner-login-btn"
            className="btn-gold w-full justify-center mt-3">
            {loading ? 'Loading...' : 'Continue'}
          </button>
        </div>
      </div>
    );
  }

  const tenantBrand = brand?.tenant?.brand || {};
  const colorOr = (c, fallback) => (/^#[0-9a-fA-F]{6}$/.test(c || '') ? c : fallback);
  const primary = colorOr(tenantBrand.colors?.primary, '#070B5F');
  const accent = colorOr(tenantBrand.colors?.accent, '#6BA82E');
  const tenantName = brand?.tenant?.name || 'BrokerForYou';

  return (
    <div className="min-h-screen bg-slate-50 p-6" data-testid="partner-portal-page">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Co-branded header */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6" data-testid="partner-header">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-4 min-w-0">
              {tenantBrand.logo_url && (
                <img src={brandAssetUrl(tenantBrand.logo_url)} alt={tenantName} className="h-12 max-w-[140px] object-contain" data-testid="partner-brand-logo" />
              )}
              <div className="min-w-0">
                <div className="text-xs uppercase tracking-widest font-semibold" style={{ color: accent }}>Referral Partner Portal</div>
                <div className="text-xl font-serif font-semibold break-words" style={{ color: primary }} data-testid="partner-brand-title">
                  {me.name} × {tenantName}
                </div>
                <div className="text-sm text-slate-500 mt-0.5" data-testid="partner-subtitle">
                  {[me.company, labelFor(PARTNER_KIND_LABELS, me.kind)].filter(Boolean).join(' · ')}
                </div>
              </div>
            </div>
            <button type="button" onClick={signOut} data-testid="partner-signout-btn" className="shrink-0 inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900"><LogOut size={14} /> Sign out</button>
          </div>
          <div className="mt-5 pt-5 border-t border-slate-100 grid grid-cols-2 gap-4 max-w-sm">
            <div data-testid="partner-total">
              <div className="text-xs uppercase tracking-widest text-slate-500">Total referrals</div>
              <div className="text-2xl font-serif font-bold mt-0.5" style={{ color: primary }}>{me.total_referrals || 0}</div>
            </div>
            <div data-testid="partner-closed">
              <div className="text-xs uppercase tracking-widest text-slate-500">Closed</div>
              <div className="text-2xl font-serif font-bold mt-0.5" style={{ color: accent }}>{me.closed_referrals || 0}</div>
            </div>
          </div>
        </div>

        {/* Submit form */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
          <h2 className="font-serif text-lg font-semibold mb-4" style={{ color: primary }}>Refer a borrower</h2>
          {/* Typed on purpose: the Partner is an external seat, so no tenant contact search here (156). */}
          <div className="grid md:grid-cols-2 gap-3">
            <div><Label className="text-sm" style={{ color: primary }}>First Name *</Label><Input value={form.borrower_first_name} onChange={(e) => setForm({ ...form, borrower_first_name: e.target.value })} className="mt-1.5" data-testid="ref-first" /></div>
            <div><Label className="text-sm" style={{ color: primary }}>Last Name *</Label><Input value={form.borrower_last_name} onChange={(e) => setForm({ ...form, borrower_last_name: e.target.value })} className="mt-1.5" data-testid="ref-last" /></div>
            <div><Label className="text-sm" style={{ color: primary }}>Email</Label><Input value={form.borrower_email} onChange={(e) => setForm({ ...form, borrower_email: e.target.value })} className="mt-1.5" data-testid="ref-email" aria-invalid={emailBad} />{emailBad && <p className="text-xs text-red-700 mt-1" data-testid="ref-email-error">Enter a valid email</p>}</div>
            <div><Label className="text-sm" style={{ color: primary }}>Phone</Label><PhoneInput value={form.borrower_phone} onChange={(d) => setForm({ ...form, borrower_phone: d })} className="mt-1.5" /></div>
            <div>
              <Label className="text-sm" style={{ color: primary }}>Loan Purpose</Label>
              <Select value={form.loan_purpose} onValueChange={(v) => setForm({ ...form, loan_purpose: v })}>
                <SelectTrigger className="mt-1.5" data-testid="ref-purpose"><SelectValue /></SelectTrigger>
                <SelectContent>{LOAN_PURPOSES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm" style={{ color: primary }}>Urgency</Label>
              <Select value={form.urgency} onValueChange={(v) => setForm({ ...form, urgency: v })}>
                <SelectTrigger className="mt-1.5" data-testid="ref-urgency"><SelectValue /></SelectTrigger>
                <SelectContent>{URGENCIES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-sm" style={{ color: primary }}>Estimated Price</Label><MoneyInput value={form.estimated_price} onChange={(n) => setForm({ ...form, estimated_price: n })} className="mt-1.5" /></div>
            <div className="md:col-span-2"><Label className="text-sm" style={{ color: primary }}>Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="mt-1.5" /></div>
          </div>
          <div className="flex justify-end mt-4">
            <button onClick={submit} disabled={submitting || !canSubmit}
              data-testid="ref-submit-btn"
              className="btn-gold">
              {submitting ? <Loader2 className="animate-spin" size={14} /> : <Send size={14} />} Submit
            </button>
          </div>
        </div>

        {/* Referrals list — financials redacted server-side */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp style={{ color: accent }} size={16} />
            <h2 className="text-sm font-medium" style={{ color: primary }}>Your referrals ({referrals.length})</h2>
          </div>
          {referrals.length === 0 ? (
            <p className="text-xs text-slate-400">No referrals submitted yet.</p>
          ) : (
            <ul className="divide-y divide-slate-100">
              {referrals.map((r, i) => (
                <li key={r.referral_id || i} className="py-3">
                  <div className="text-sm" data-testid={`ref-name-${r.referral_id}`}>{r.borrower_name}</div>
                  <div className="text-xs text-slate-500" data-testid={`ref-stage-${r.referral_id}`}>
                    Status: {r.stage_label}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
